import { useCallback, useEffect, useState } from 'react';

import { skipToken, useQuery } from '@tanstack/react-query';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import { MarketplaceBrowseMapDocument } from 'components/marketplace/browse/__generated__/marketplace-browse.zeus';
import type { MarketplaceMapSearchInput } from 'components/marketplace/browse/__generated__/marketplace-browse.zeus';
import {
  marketplaceMapQueryKey,
  selectMapResult,
} from 'components/marketplace/browse/marketplaceBrowse';
import type { MarketplaceBrowseVariables } from 'components/marketplace/browse/marketplaceBrowse';
import { normalizeSearchVertices } from 'components/marketplace/helpers/useSearchQueries';
import { viewportSearchArea } from 'components/marketplace/helpers/viewportSearchArea';

import type { GeoPoint } from '../types';
import { reportMapError } from './reportMapError';

type SearchInput = MarketplaceBrowseVariables['listInput'];

/** Property focus changes the map viewport while the browse list and URL retain their search. */
export const useMapPreviewResult = ({
  searchInput,
  enabled,
}: {
  searchInput: SearchInput;
  enabled: boolean;
}) => {
  const [preview, setPreview] = useState<{
    searchInput: SearchInput;
    addressId: string;
    input: MarketplaceMapSearchInput;
  } | null>(null);
  const input = enabled && preview?.searchInput === searchInput ? preview.input : null;
  const query = useQuery({
    queryKey: [...marketplaceMapQueryKey, input, false],
    queryFn: input
      ? ({ signal }) =>
          requestZeusFromBrowser(
            MarketplaceBrowseMapDocument,
            { input, includePhotos: false },
            signal,
          )
      : skipToken,
    select: selectMapResult,
    staleTime: 30_000,
    gcTime: 30_000,
    retry: 1,
  });
  const refresh = useCallback(
    (coordinates: GeoPoint[], addressId: string) => {
      const viewport = normalizeSearchVertices(coordinates);
      if (!enabled || !viewport) return;
      setPreview({
        searchInput,
        addressId,
        input: {
          filters: searchInput.filters,
          mode: { adaptive: { viewport, area: viewportSearchArea(viewport), markerLimit: 500 } },
        },
      });
    },
    [enabled, searchInput],
  );
  useEffect(() => {
    if (query.error)
      reportMapError('marketplace-browse', query.error, { operation: 'propertyFocusMap' });
  }, [query.error]);
  return {
    result: input ? query.data : undefined,
    focusedAddressId: input ? preview?.addressId : undefined,
    refresh,
  };
};
